import type { Background } from '@/api/types/background'
import {
  type FilenameMatchRank,
  matchBackgroundFilename,
} from '@/lib/backgrounds/match-background-filename'

/** How many suggestions the "Search by filename" dropdown shows at most. */
export const MAX_BACKGROUND_FILENAME_SUGGESTIONS = 8

/**
 * Filename suggestions for the Backgrounds tray's "Search by filename" field: the distinct
 * `displayName`s that match `query` under the same fuzzy rule the grid filter uses
 * (`matchBackgroundFilename`), so every suggestion has a result. Closest matches come first; within
 * each closeness group, names that start with the query lead, then the rest, alphabetically. Empty
 * for a blank query, and empty when the only match is exactly what's already typed (nothing left
 * to suggest).
 */
export function suggestBackgroundFilenames(
  backgrounds: Background[],
  query: string,
  limit = MAX_BACKGROUND_FILENAME_SUGGESTIONS
): string[] {
  const needle = query.trim().toUpperCase()

  if (!needle) return []

  const seen = new Set<string>()
  const matches: Array<{ name: string; rank: FilenameMatchRank; startsWithQuery: boolean }> = []

  for (const { displayName } of backgrounds) {
    const name = displayName.trim()
    const key = name.toUpperCase()

    if (!name || seen.has(key)) continue

    const rank = matchBackgroundFilename(name, query)

    if (rank === null) continue
    seen.add(key)
    matches.push({ name, rank, startsWithQuery: key.startsWith(needle) })
  }

  const suggestions = matches
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        Number(b.startsWithQuery) - Number(a.startsWithQuery) ||
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
    .slice(0, limit)
    .map((match) => match.name)

  if (suggestions.length === 1 && suggestions[0].toUpperCase() === needle) return []

  return suggestions
}
